import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar';
import BusinessSidebar from './BusinessSidebar';
import Header from './Header';
import AdminHeader from './AdminHeader';
import PublicHeader from './PublicHeader';
import { useSector } from '../../contexts/SectorContext';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
import NotificationPermissionBanner from '../common/NotificationPermissionBanner';
import { db } from '../../services/firebase';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { stopNotificationSound } from '../../services/fcmService';
import BusinessSelectionModal from '../dashboard/BusinessSelectionModal';
import { isAdminUser } from '../../utils/userUtils';
import { SparklesIcon, XMarkIcon, CogIcon, ArrowPathIcon, ChatBubbleLeftRightIcon, BellAlertIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/solid';

import AIAssistantWidget from '../AIAssistantWidget';
import WhatsAppAssistantWidget from '../WhatsAppAssistantWidget';
import NetworkStatusBanner from '../common/NetworkStatusBanner';

// 🎯 Sürüklenebilir Hızlı Asistan Widget'ı (Draggable Floating Widget)
const DraggableFloatingWidget = ({ hideWidgets, setHideWidgets }) => {
  const navigate = useNavigate();
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [showAssistantMenu, setShowAssistantMenu] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // 'ai' | 'wa' | null
  const [isHovered, setIsHovered] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(() => {
    const saved = localStorage.getItem('show_whatsapp_assistant');
    return saved !== null ? saved === 'true' : true;
  });
  const [showAi, setShowAi] = useState(() => {
    const saved = localStorage.getItem('show_ai_assistant');
    return saved !== null ? saved === 'true' : true;
  });
  const [dragEnabled, setDragEnabled] = useState(() => {
    const saved = localStorage.getItem('floating_widget_drag_enabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [edgeSnapEnabled, setEdgeSnapEnabled] = useState(() => {
    const saved = localStorage.getItem('floating_widget_edge_snap');
    return saved !== null ? saved === 'true' : true;
  });

  // 🔴 Canlı Okunmamış WhatsApp / Destek Mesaj Sayısı (Badge)
  const { selectedBusiness } = useAuthBusiness();
  const [unreadWhatsAppCount, setUnreadWhatsAppCount] = useState(0);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    
    // 1. Bekleyen operator talepleri ve okunmamış sohbetleri dinle
    const convRef = collection(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'conversations');
    const q = query(convRef, where('operator_needed', '==', true));
    
    const unsubscribe = onSnapshot(q, (snap) => {
      setUnreadWhatsAppCount(snap.size);
    }, (err) => {
      console.warn('Draggable floating widget conversations listener error:', err);
    });

    // 2. Global event dinleyicisi (gelen anlık mesajda rozeti artır)
    const handleIncrement = () => {
      setUnreadWhatsAppCount(prev => prev + 1);
    };
    window.addEventListener('whatsapp_unread_increment', handleIncrement);

    return () => {
      unsubscribe();
      window.removeEventListener('whatsapp_unread_increment', handleIncrement);
    };
  }, [selectedBusiness?.id]);

  const [widgetPos, setWidgetPos] = useState(() => {
    try {
      const saved = localStorage.getItem('floating_widget_pos');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1000;
    return {
      x: Math.max(20, screenWidth - 70), 
      y: typeof window !== 'undefined' ? window.innerHeight - 100 : 600
    };
  });

  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const initialPosRef = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);
  const longPressTimerRef = useRef(null);
  const isLongPressRef = useRef(false);

  useEffect(() => {
    const handleStorageChange = () => {
      const savedDrag = localStorage.getItem('floating_widget_drag_enabled');
      setDragEnabled(savedDrag !== null ? savedDrag === 'true' : true);
      const savedSnap = localStorage.getItem('floating_widget_edge_snap');
      setEdgeSnapEnabled(savedSnap !== null ? savedSnap === 'true' : true);
      const savedWa = localStorage.getItem('show_whatsapp_assistant');
      setShowWhatsapp(savedWa !== null ? savedWa === 'true' : true);
      const savedAi = localStorage.getItem('show_ai_assistant');
      setShowAi(savedAi !== null ? savedAi === 'true' : true);
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const snapToEdge = (pos) => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1000;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
    const widgetWidth = 44;
    const midpoint = screenWidth / 2;

    const clampedY = Math.max(20, Math.min(screenHeight - 110, pos.y));
    let snappedX;
    if (pos.x + widgetWidth / 2 < midpoint) {
      snappedX = 16; // Sol kenara kilitle
    } else {
      snappedX = Math.max(20, screenWidth - 64); // Sağ kenara güvenli kilitle (ekran dışına taşmaz)
    }

    return { x: snappedX, y: clampedY };
  };

  const clampPosition = (pos) => {
    return edgeSnapEnabled ? snapToEdge(pos) : pos;
  };

  useEffect(() => {
    const handleResize = () => {
      setWidgetPos(prev => clampPosition(prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [hideWidgets, edgeSnapEnabled]);

  const handleStart = (clientX, clientY) => {
    if (showQuickMenu || showAssistantMenu) return;

    isLongPressRef.current = false;
    hasDraggedRef.current = false;

    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      try {
        if (navigator.vibrate) navigator.vibrate(50);
      } catch (e) {}
      setShowQuickMenu(prev => !prev);
    }, 600);

    if (dragEnabled) {
      setIsDragging(true);
      dragStartRef.current = { x: clientX, y: clientY };
      initialPosRef.current = { ...widgetPos };
    }
  };

  const handleMove = (clientX, clientY) => {
    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      hasDraggedRef.current = true;
    }
    if (!isDragging || !dragEnabled) return;
    const rawPos = {
      x: initialPosRef.current.x + dx,
      y: initialPosRef.current.y + dy
    };
    setWidgetPos(rawPos);
  };

  const handleEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setIsDragging(false);

    setWidgetPos(prev => {
      const finalPos = clampPosition(prev);
      try {
        localStorage.setItem('floating_widget_pos', JSON.stringify(finalPos));
      } catch (e) {}
      return finalPos;
    });
  };

  useEffect(() => {
    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onMouseUp = () => handleEnd();
    const onTouchMove = (e) => {
      if (e.touches && e.touches.length > 0) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const onTouchEnd = () => handleEnd();

    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDragging, dragEnabled, edgeSnapEnabled]);

  const screenW = typeof window !== 'undefined' ? window.innerWidth : 1000;
  const isDockedLeft = widgetPos.x < 30;
  const isDockedRight = widgetPos.x > screenW - 60;
  const isDocked = (isDockedLeft || isDockedRight) && edgeSnapEnabled;

  let transformShift = 'translateX(0px)';
  if (isDocked) {
    if (!isHovered && !showQuickMenu && !showAssistantMenu) {
      if (isDockedLeft) transformShift = 'translateX(-12px)';
      if (isDockedRight) transformShift = 'translateX(12px)';
    }
  }

  return (
    <>
      {(showAssistantMenu || showQuickMenu) && createPortal(
        <div 
          className="fixed inset-0 z-[999990] bg-black/10 backdrop-blur-[1px]"
          onClick={() => {
            setShowAssistantMenu(false);
            setShowQuickMenu(false);
          }}
        />,
        document.body
      )}

      {/* Modal açıkken arkadaki sürüklenebilir butonu gizle */}
      {!activeModal && (
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            position: 'fixed',
            left: `${widgetPos.x}px`,
            top: `${widgetPos.y}px`,
            transform: transformShift,
            transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            zIndex: 999995,
            touchAction: 'none',
            cursor: dragEnabled ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
            opacity: (!isHovered && !showQuickMenu && !showAssistantMenu) ? 0.75 : 1
          }}
        onMouseDown={(e) => {
          if (e.button === 0) handleStart(e.clientX, e.clientY);
        }}
        onTouchStart={(e) => {
          if (e.touches.length > 0) handleStart(e.touches[0].clientX, e.touches[0].clientY);
        }}
        onClickCapture={(e) => {
          if (hasDraggedRef.current || isLongPressRef.current) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        className="no-print select-none relative"
      >
        <button
          onClick={() => {
            if (!hasDraggedRef.current && !isLongPressRef.current) {
              setShowAssistantMenu(prev => !prev);
            }
          }}
          className="w-10 h-10 bg-gradient-to-tr from-violet-600 via-purple-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 text-white rounded-full flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-transform duration-200 border-2 border-white/50 cursor-pointer"
          title="Asistan Menüsü (Tıklayarak açabilirsiniz)"
        >
          <div className="relative w-5 h-5 flex items-center justify-center pointer-events-none">
            <div className="absolute w-2 h-2 bg-white rounded-full shadow-[0_0_6px_rgba(255,255,255,0.9)]"></div>
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.4" strokeDasharray="2 2" />
              <circle cx="12" cy="12" r="6" stroke="currentColor" strokeOpacity="0.6" />
            </svg>
          </div>

          {/* 🔴 Buton Kapalıyken Sağ Üst Bildirim Rozeti (Badge) */}
          {unreadWhatsAppCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-rose-600 border-2 border-white rounded-full text-[10px] font-black text-white flex items-center justify-center shadow-lg animate-bounce">
              {unreadWhatsAppCount > 99 ? '99+' : unreadWhatsAppCount}
            </span>
          )}
        </button>

      {/* 📱 Mobile & Desktop Edge-Anchored Popover Menu */}
      {showAssistantMenu && createPortal(
        <div
          className="fixed w-72 bg-white/95 backdrop-blur-2xl text-gray-900 rounded-3xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-100 z-[9999999] animate-in fade-in zoom-in-95 duration-200 space-y-3"
          style={{
            top: `${Math.max(10, Math.min((typeof window !== 'undefined' ? window.innerHeight : 800) - 240, widgetPos.y - 90))}px`,
            left: isDockedLeft
              ? '55px'
              : `${Math.max(10, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1000) - 300, widgetPos.x - 290))}px`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
            <span className="text-xs font-black text-gray-900 flex items-center gap-1.5 uppercase tracking-wider">
              <SparklesIcon className="w-4 h-4 text-violet-600" /> Destek & Asistan
            </span>
            <button
              onClick={() => setShowAssistantMenu(false)}
              className="text-gray-400 hover:text-gray-700 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2">
            {showAi && (
              <button
                onClick={() => {
                  setShowAssistantMenu(false);
                  setActiveModal('ai');
                }}
                className="w-full p-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-2xl flex items-center gap-3 transition-all active:scale-95 shadow-md shadow-violet-500/20"
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-none">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
                <div className="text-left min-w-0">
                  <span className="text-xs font-black block leading-tight truncate">AI Yönetim Asistanı</span>
                  <span className="text-[10px] text-violet-100 font-medium block truncate">Soru sor, analiz & rapor al</span>
                </div>
              </button>
            )}

            {showWhatsapp && (
              <button
                onClick={() => {
                  setShowAssistantMenu(false);
                  setUnreadWhatsAppCount(0); // Açıldığında sayacı sıfırla
                  setActiveModal('wa');
                }}
                className="w-full p-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-2xl flex items-center justify-between transition-all active:scale-95 shadow-md shadow-emerald-500/20"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-none">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left min-w-0">
                    <span className="text-xs font-black block leading-tight truncate">WhatsApp Asistanı</span>
                    <span className="text-[10px] text-emerald-100 font-medium block truncate">Canlı mesaj & müşteri desteği</span>
                  </div>
                </div>

                {/* 🟢 Menü Açıkken WhatsApp Kartı İçi Sayaç Rozeti */}
                {unreadWhatsAppCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-white text-emerald-700 text-[10px] font-black rounded-full shadow-sm shrink-0">
                    {unreadWhatsAppCount} Yeni
                  </span>
                )}
              </button>
            )}

            <button
              onClick={() => {
                setShowAssistantMenu(false);
                setShowQuickMenu(true);
              }}
              className="w-full p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-2xl text-xs font-extrabold flex items-center justify-between transition-colors border border-gray-100"
            >
              <span className="flex items-center gap-1.5 text-xs"><CogIcon className="w-4 h-4 text-violet-600" /> Sürükleme / Widget Ayarları</span>
              <span className="text-[10px] text-gray-400">⚙️</span>
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* ⚙️ Hızlı Ayarlar Popup */}
      {showQuickMenu && createPortal(
        <div
          className="fixed w-72 bg-white/95 backdrop-blur-2xl text-gray-900 rounded-3xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-100 z-[9999999] animate-in fade-in zoom-in-95 duration-200 space-y-3"
          style={{
            top: `${Math.max(10, Math.min((typeof window !== 'undefined' ? window.innerHeight : 800) - 280, widgetPos.y - 120))}px`,
            left: isDockedLeft
              ? '55px'
              : `${Math.max(10, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1000) - 300, widgetPos.x - 290))}px`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between pb-2 border-b border-gray-100">
            <span className="text-xs font-black text-gray-900 flex items-center gap-1.5 uppercase">
              <SparklesIcon className="w-4 h-4 text-violet-600" /> Uygulama Ayarları
            </span>
            <button
              onClick={() => setShowQuickMenu(false)}
              className="text-gray-400 hover:text-gray-700 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-1.5 text-xs font-bold text-gray-700">
            <button
              onClick={() => {
                setShowQuickMenu(false);
                navigate('/sectors/food-beverage/settings/app-popup');
              }}
              className="w-full text-left px-3 py-2 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-xl flex items-center justify-between transition-colors"
            >
              <span className="flex items-center gap-2 font-black">
                <CogIcon className="w-4 h-4 text-violet-600" /> Tüm Ayarlar
              </span>
              <span className="text-[10px] bg-violet-600 px-2 py-0.5 rounded-md text-white font-bold">Git ➔</span>
            </button>

            <button
              onClick={() => {
                const nextSnap = !edgeSnapEnabled;
                setEdgeSnapEnabled(nextSnap);
                localStorage.setItem('floating_widget_edge_snap', nextSnap ? 'true' : 'false');
                if (nextSnap) setWidgetPos(prev => snapToEdge(prev));
              }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-xl flex items-center justify-between transition-colors border border-gray-100"
            >
              <span>Kenara Saklanma: {edgeSnapEnabled ? 'Aktif' : 'Pasif'}</span>
              <span className={`w-2.5 h-2.5 rounded-full ${edgeSnapEnabled ? 'bg-emerald-500' : 'bg-gray-300'}`} />
            </button>

            <button
              onClick={() => {
                const nextDrag = !dragEnabled;
                setDragEnabled(nextDrag);
                localStorage.setItem('floating_widget_drag_enabled', nextDrag ? 'true' : 'false');
              }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-xl flex items-center justify-between transition-colors border border-gray-100"
            >
              <span>Sürükleme: {dragEnabled ? 'Aktif' : 'Kilitli'}</span>
              <span className={`w-2.5 h-2.5 rounded-full ${dragEnabled ? 'bg-emerald-500' : 'bg-rose-400'}`} />
            </button>

            <button
              onClick={() => {
                const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1000;
                const defaultPos = {
                  x: screenWidth - 20,
                  y: typeof window !== 'undefined' ? window.innerHeight - 100 : 600
                };
                setWidgetPos(defaultPos);
                localStorage.setItem('floating_widget_pos', JSON.stringify(defaultPos));
                setShowQuickMenu(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-xl flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4 text-gray-400" /> Konumu Sıfırla
            </button>
          </div>
        </div>,
        document.body
      )}
      </div>
      )}

      {activeModal === 'ai' && (
        <AIAssistantWidget
          isEmbedded={false}
          isOpen={true}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'wa' && (
        <WhatsAppAssistantWidget
          isEmbedded={false}
          isOpen={true}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
};

const isMainHubPage = (pathname) => {
  if (!pathname) return false;
  // Sektörel sayfaların ve genel işletme sayfalarının hemen hepsinde görünmesini sağla
  const publicPaths = ['/login', '/register', '/landing', '/auth/callback', '/verify-email', '/setup', '/plans', '/checkout'];
  if (publicPaths.some(p => pathname.startsWith(p))) return false;
  return true;
};

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [businessSidebarCollapsed, setBusinessSidebarCollapsed] = useState(() => {
    return localStorage.getItem('businessSidebarCollapsed') === 'true';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [hideWidgets, setHideWidgets] = useState(() => {
    const saved = localStorage.getItem('hide_assistants');
    return saved !== null ? saved === 'true' : true;
  });
  const location = useLocation();
  const { currentSector } = useSector();

  // 🎯 AuthBusinessContext'ten state al
  const {
    currentUser,
    currentUserRole,
    selectedBusiness,
    showSidebar,
    sidebarType,
    headerMode,
    isAuthenticated,
    isLoading,
    memberships,
    switchBusiness
  } = useAuthBusiness();

  const [isMobileChatActive, setIsMobileChatActive] = useState(false);

  // 📱 WhatsApp ve mobil sohbet detayındayken Master Header'ı dinamik gizle
  useEffect(() => {
    const handleMobileChatState = (e) => {
      setIsMobileChatActive(!!e.detail?.active);
    };
    window.addEventListener('whatsapp_mobile_chat_state', handleMobileChatState);
    return () => window.removeEventListener('whatsapp_mobile_chat_state', handleMobileChatState);
  }, []);

  // Dark mode localStorage persistence
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
  }, []);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
  };

  // 📱 Sayfa değiştiğinde mobil sidebar'ı otomatik kapat
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // ⏰ CANLI HATIRLATICI DİNLEYİCİSİ & POP-UP BİLDİRİM SİSTEMİ (Cron / Interval Destekli)
  const [activeReminderAlert, setActiveReminderAlert] = useState(null);
  const notifiedReminderIdsRef = useRef(new Set());
  const pendingRemindersRef = useRef([]);

  useEffect(() => {
    if (!selectedBusiness?.id || !isAuthenticated) return;

    // 1. Zamanı gelen hatırlatıcıları kontrol eden motor (Hem anlık hem cron döngüsü)
    const checkDueReminders = () => {
      const now = new Date();
      const currentHours = String(now.getHours()).padStart(2, '0');
      const currentMinutes = String(now.getMinutes()).padStart(2, '0');
      const currentTimeStr = `${currentHours}:${currentMinutes}`;
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      pendingRemindersRef.current.forEach((item) => {
        if (notifiedReminderIdsRef.current.has(item.id)) return;

        const remDate = item.date || todayStr;
        const remTime = item.time || '00:00';

        // Bugün veya geçmiş tarihli, ve vakti gelmiş ya da geçmiş olanları tetikle
        const isPastOrToday = remDate <= todayStr;
        const isTimeDue = (remDate < todayStr) || (remDate === todayStr && remTime <= currentTimeStr);

        if (isPastOrToday && isTimeDue) {
          notifiedReminderIdsRef.current.add(item.id);
          setActiveReminderAlert(item);

          // 1. Tarayıcı Push Bildirimi (Notification API)
          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            try {
              new Notification('⏰ Hatırlatıcı Vakti Geldi!', {
                body: `${item.title} (${remTime})`,
                icon: '/favicon.ico',
                tag: item.id
              });
            } catch (notifErr) {
              console.warn('Browser notification error:', notifErr);
            }
          }

          // 2. Sesli Bildirim
          try {
            const audio = new Audio('/sounds/notification.mp3');
            audio.play().catch(() => {});
          } catch (e) {}
        }
      });
    };

    // 2. İşletmeye ait hatırlatıcıları canlı dinle
    const remRef = collection(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'ai_reminders');
    const q = query(remRef, where('status', 'in', ['pending', 'overdue']));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      pendingRemindersRef.current = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      // Veri geldiğinde veya değiştiğinde hemen kontrol et
      checkDueReminders();
    }, (err) => {
      console.warn('ai_reminders snapshot error:', err);
    });

    // 3. ⏱️ CRON / ARKA PLAN SAYACI (Her 10 saniyede bir kontrol et - Sayfa açıkken zaman dolduğunda anında yakalar!)
    const cronInterval = setInterval(() => {
      checkDueReminders();
    }, 10000);

    return () => {
      unsubscribe();
      clearInterval(cronInterval);
    };
  }, [selectedBusiness?.id, isAuthenticated]);

  // Hatırlatıcıyı Tamamlandı Yap
  const handleCompleteReminderAlert = async (reminderId) => {
    if (!selectedBusiness?.id || !reminderId) return;
    try {
      await updateDoc(doc(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'ai_reminders', reminderId), {
        status: 'done',
        completedAt: new Date()
      });
    } catch (e) {
      console.error('Reminder complete error:', e);
    } finally {
      setActiveReminderAlert(null);
    }
  };

  // Hatırlatıcıyı 15 Dakika Ertele
  const handleSnoozeReminderAlert = async (reminderItem) => {
    if (!selectedBusiness?.id || !reminderItem?.id) return;
    try {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 15);
      const newTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const newDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      // Set'ten çıkar ki 15 dk sonra tekrar uyarsın
      notifiedReminderIdsRef.current.delete(reminderItem.id);

      await updateDoc(doc(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'ai_reminders', reminderItem.id), {
        time: newTime,
        date: newDate,
        status: 'pending'
      });
    } catch (e) {
      console.error('Reminder snooze error:', e);
    } finally {
      setActiveReminderAlert(null);
    }
  };

  // 🔔 Ses senkronizasyonu artık GlobalOrderOverlay bileşeni tarafından tek merkezden yönetilmektedir.
  // (Mükerrer Firestore onSnapshot okumasını engellemek için kaldırıldı)

  // 🔄 GÜNCELLEME: Bildirimden gelen businessId ile otomatik işletme geçişi
  useEffect(() => {
    if (!isAuthenticated) return;

    const searchParams = new URLSearchParams(location.search);
    const targetBusinessId = searchParams.get('businessId');

    if (targetBusinessId && selectedBusiness?.id !== targetBusinessId) {
      console.log('🚀 DashboardLayout: Auto-switching business via Deep Link:', targetBusinessId);
      switchBusiness(targetBusinessId);
      
      // Parametreyi URL'den temizle (döngü engellemek ve temiz URL için)
      searchParams.delete('businessId');
      const newQuery = searchParams.toString();
      const newUrl = window.location.pathname + (newQuery ? '?' + newQuery : '');
      window.history.replaceState(null, '', newUrl);
    }
  }, [location.search, selectedBusiness?.id, isAuthenticated, switchBusiness]);

  // 🎯 SIDEBAR VISIBILITY LOGIC - AuthBusinessContext'ten kontrol (Cari kullanıcılar için her zaman gizle)
  const isCari = currentUserRole === 'cari' || currentUser?.role === 'cari' || currentUser?.user_type === 'cari';
  const shouldShowSidebar = showSidebar && isAuthenticated && !isCari;

  const isStandalonePage = () => {
    const path = location.pathname;
    return (
      path.includes('/courier-app') ||
      path.includes('/production-terminal') ||
      path.endsWith('/uretim') ||
      path.endsWith('/toptan') ||
      path.includes('/kitchen/') ||
      path.includes('/kds') ||
      path.includes('/portal') ||
      path.includes('/image-editor') ||
      path.includes('/design-studio')
    );
  };

  if (isStandalonePage()) {
    return <>{children}</>;
  }

  // 🎯 EFFECTIVE SIDEBAR TYPE: URL-first override (switching race condition fix)
  const getEffectiveSidebarType = () => {
    const path = location.pathname;

    // Sektörel URL'lerde her zaman ilgili sektörel sidebar
    if (path.startsWith('/sectors/food-beverage')) return 'sectoral_food-beverage';
    if (path.startsWith('/sectors/retail')) return 'sectoral_retail';
    if (path.startsWith('/sectors/service')) return 'sectoral_service';

    // İşletme WhatsApp sayfalarında işletmenin sektörel sidebar'ını koru
    if (path.startsWith('/business/whatsapp')) {
      if (sidebarType?.includes('retail') || selectedBusiness?.sector === 'retail') return 'sectoral_retail';
      if (sidebarType?.includes('service') || selectedBusiness?.sector === 'service') return 'sectoral_service';
      return 'sectoral_food-beverage';
    }

    if (
      path === '/brands' ||
      path.startsWith('/brands/') ||
      path.startsWith('/subscription/') ||
      path === '/subscription' ||
      path === '/dashboard/personal' ||
      path.startsWith('/dashboard/personal') ||
      path === '/business-overview' ||
      path.startsWith('/business-overview/') ||
      path === '/financial-status' ||
      path.startsWith('/financial-status/')
    ) {
      return 'business';
    }

    // Sadece Admin URL'lerinde admin sidebar
    if (path.startsWith('/dashboard/admin')) {
      return 'admin';
    }

    // Seçili işletme varsa ve sektörü biliniyorsa sektörel sidebar kullan
    if (selectedBusiness?.sector) {
      const s = selectedBusiness.sector;
      if (s === 'retail') return 'sectoral_retail';
      if (s === 'service') return 'sectoral_service';
      return 'sectoral_food-beverage';
    }

    // Geri kalanlar için context değerini kullan
    return sidebarType || 'sectoral_food-beverage';
  };
  const effectiveSidebarType = getEffectiveSidebarType();

  // 🎯 PUBLIC PAGE CHECK
  const isPublicPage = () => {
    const publicPaths = [
      '/login', '/register', '/landing', '/auth/callback',
      '/verify-email', '/plans', '/checkout', '/setup'
    ];
    return publicPaths.includes(location.pathname);
  };

  // 🛡️ POS / Satış Ekranlarında Global Header gizlenir ve sayfa viewport'a tam oturur (dahili bağımsız scroll)
  const isPosScreen = 
    location.pathname.includes('/pos') ||
    location.pathname.includes('/customer-order') ||
    location.pathname.includes('/phone-orders') ||
    location.pathname.includes('/table-service') ||
    location.pathname.includes('/table-detail') ||
    location.pathname.includes('/quick-sales') ||
    location.pathname.includes('/counter') ||
    location.pathname.includes('/tezgah') ||
    location.pathname.includes('/weight-sales');

  const isWhatsAppPage = location.pathname === '/business/whatsapp' || location.pathname.startsWith('/business/whatsapp/');

  // 🎯 HEADER SELECTION LOGIC
  const getHeaderComponent = (authLoading) => {
    // If auth is still loading, show nothing to prevent PublicHeader flash
    if (authLoading) return null;

    if (isPosScreen || isWhatsAppPage) {
      return null;
    }

    // Public pages
    if (!isAuthenticated || isPublicPage()) {
      return <PublicHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} />;
    }

    // Admin header - Sadece admin sayfalarında veya headerMode admin ise göster
    const isAdminPath = location.pathname.startsWith('/dashboard/admin');
    if (isAdminPath || headerMode === 'admin' || effectiveSidebarType === 'admin') {
      return (
        <AdminHeader
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
        />
      );
    }

    // Business header (default)
    return (
      <Header
        key={currentUser?.uid || 'guest'} // Force re-render on user change
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />
    );
  };

  // 🎯 SIDEBAR SELECTION LOGIC
  const getSidebarComponent = (isMobile = false) => {
    if (!shouldShowSidebar) return null;

    // Admin rotalarında admin sidebar: Masaüstünde gizlidir, mobilde ise menüden açılır
    const isAdminPath = location.pathname.startsWith('/dashboard/admin');
    if (isAdminPath || effectiveSidebarType === 'admin') {
      if (isMobile) {
        return (
          <Sidebar
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            darkMode={darkMode}
            isCollapsed={false}
            onCollapse={setSidebarCollapsed}
          />
        );
      }
      return null;
    }

    // Business account sidebar
    if (effectiveSidebarType === 'business' || effectiveSidebarType === 'business_management') {
      return (
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          darkMode={darkMode}
          isCollapsed={isMobile ? false : sidebarCollapsed}
          onCollapse={setSidebarCollapsed}
        />
      );
    }

    // Sectoral sidebars
    if (['sectoral_food-beverage', 'sectoral_retail', 'sectoral_service', 'food', 'restaurant', 'retail', 'service'].includes(effectiveSidebarType)) {
      let normalizedSectorType = effectiveSidebarType;
      if (effectiveSidebarType === 'food' || effectiveSidebarType === 'restaurant') {
        normalizedSectorType = 'sectoral_food-beverage';
      } else if (effectiveSidebarType === 'retail') {
        normalizedSectorType = 'sectoral_retail';
      } else if (effectiveSidebarType === 'service') {
        normalizedSectorType = 'sectoral_service';
      }

      return (
        <BusinessSidebar
          isCollapsed={isMobile ? false : businessSidebarCollapsed}
          onToggle={() => {
            const newVal = !businessSidebarCollapsed;
            setBusinessSidebarCollapsed(newVal);
            localStorage.setItem('businessSidebarCollapsed', String(newVal));
            window.dispatchEvent(new CustomEvent('businessSidebarCollapse', { detail: newVal }));
          }}
          onClose={() => setSidebarOpen(false)}
          darkMode={darkMode}
          sectorType={normalizedSectorType}
        />
      );
    }

    // Default fallback
    return (
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        darkMode={darkMode}
        isCollapsed={isMobile ? false : sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
      />
    );
  };

  const isAIPostPage = location.pathname === '/social-media/ai-post';
  const isCourierTrackingPage = location.pathname === '/sectors/food-beverage/operations/courier-tracking';
  const isOrderHeatmapPage = location.pathname === '/sectors/food-beverage/reports/order-heatmap';
  const isEmailCenter = location.pathname.includes('/email-center') || location.pathname.includes('/admin/emails') || location.pathname.includes('/admin/mail');
  const isInvestmentPage = location.pathname.includes('investment-projection');
  const isDeliverySettingsPage = location.pathname.includes('delivery-settings');
  // 🛡️ Sadece gerçek tam ekran harita, WhatsApp mesajlaşma, e-posta merkezi, yatırım projeksiyonu, teslimat ayarları veya POS satış sayfalarında viewport'u sabitle
  // Bu sayede dahili scroll mekanizmaları ekranı %100 kaplar ve dış scroll/taşma oluşmaz
  const isFullViewport = isAIPostPage || isWhatsAppPage || isCourierTrackingPage || isOrderHeatmapPage || isPosScreen || location.pathname.includes('network-map') || isDeliverySettingsPage || isEmailCenter || isInvestmentPage;

  // 🎯 LAYOUT RENDER
  return (
    <div className={`m-0 font-sans text-base antialiased font-normal leading-default ${isFullViewport ? 'h-screen h-[100dvh] flex flex-col overflow-hidden' : 'min-h-screen'} ${darkMode ? 'dark bg-slate-900 text-white' : 'bg-gray-50 text-slate-500'}`}>
      <NetworkStatusBanner />

      {/* 📱 Mobile Master Header (Sohbetler listesindeyken görünür, sohbet detayına girince gizlenir) */}
      {(() => {
        const headerEl = getHeaderComponent(isLoading);
        if (!headerEl) return null;
        return (
          <div className={`no-print shrink-0 sticky top-0 z-40 bg-white dark:bg-[#111b21] border-b border-gray-100 dark:border-gray-800 pt-[env(safe-area-inset-top,0px)] ${
            isWhatsAppPage ? (isMobileChatActive ? 'hidden' : 'lg:hidden block') : 'lg:hidden'
          }`}>
            {headerEl}
            
            {/* Mobile Sidebar Overlay */}
            {shouldShowSidebar && sidebarOpen && (
              <div className="fixed inset-0 z-[9999]">
                {/* Background Overlay */}
                <div
                  className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'} z-[9998]`}
                  onClick={() => setSidebarOpen(false)}
                />
                
                {/* Sidebar Content */}
                <div className={`fixed inset-y-0 left-0 w-[280px] sm:w-[320px] transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} z-[9999]`}>
                  <div className="h-full shadow-2xl relative">
                    {getSidebarComponent(true)}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className={`flex ${isFullViewport ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-screen'}`}>
        {/* 🖥️ DESKTOP SIDEBAR */}
        {shouldShowSidebar && !location.pathname.startsWith('/dashboard/admin') && (
          <div className={`hidden lg:block no-print shrink-0 fixed left-0 top-0 h-screen z-40 transition-all duration-300 ${
            (['sectoral_food-beverage', 'sectoral_retail', 'sectoral_service', 'food', 'restaurant', 'retail', 'service'].includes(effectiveSidebarType) && businessSidebarCollapsed) ? "w-16" :
              (['sectoral_food-beverage', 'sectoral_retail', 'sectoral_service', 'food', 'restaurant', 'retail', 'service'].includes(effectiveSidebarType)) ? "w-80" :
                sidebarCollapsed ? "w-16" : "w-80"
            }`}>
            {getSidebarComponent()}
          </div>
        )}

        {/* 🚀 MAIN CONTENT AREA */}
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          shouldShowSidebar && !location.pathname.startsWith('/dashboard/admin')
            ? (['sectoral_food-beverage', 'sectoral_retail', 'sectoral_service', 'food', 'restaurant', 'retail', 'service'].includes(effectiveSidebarType) && businessSidebarCollapsed) ? "lg:ml-16" :
              (['sectoral_food-beverage', 'sectoral_retail', 'sectoral_service', 'food', 'restaurant', 'retail', 'service'].includes(effectiveSidebarType)) ? "lg:ml-80" :
                sidebarCollapsed ? "lg:ml-16" : "lg:ml-80"
            : ""
        } ${isFullViewport ? 'min-h-0 overflow-hidden h-full' : ''}`}>
          {(() => {
            const desktopHeaderEl = getHeaderComponent(isLoading);
            if (!desktopHeaderEl) return null;
            return (
              <div className="hidden lg:block no-print shrink-0 sticky top-0 z-40 bg-white border-b border-gray-100">
                {desktopHeaderEl}
              </div>
            );
          })()}

          <main className={`flex-1 min-h-0 flex flex-col min-w-0 ${
            isFullViewport
              ? (isEmailCenter ? "h-full max-h-full overflow-hidden p-3 sm:p-4 lg:p-5 m-0" : "h-full max-h-full overflow-hidden p-0 m-0")
              : (shouldShowSidebar && (['sectoral_food-beverage', 'sectoral_retail', 'sectoral_service', 'food', 'restaurant', 'retail', 'service'].includes(effectiveSidebarType) || location.pathname === '/social-media/ai-post' || location.pathname.includes('delivery-settings'))) 
                ? "" // Sektörel sayfalarda, AI Post ve teslimat ayarlarında padding'i kendisi yönetir
                : "p-3 sm:p-4 lg:p-6" // Diğer sayfalarda standart padding (mobilde daha az)
          }`}>
            {!isFullViewport && <NotificationPermissionBanner />}
            <div className={`flex-1 relative flex flex-col min-w-0 break-words ${
              isFullViewport
                ? "h-full max-h-full flex flex-col min-h-0 overflow-hidden rounded-none shadow-none bg-transparent"
                : (location.pathname.startsWith('/dashboard/admin') || location.pathname === '/profile' || location.pathname.startsWith('/profile/') || location.pathname === '/social-media/ai-post' || location.pathname.includes('delivery-settings') || (shouldShowSidebar && ['sectoral_food-beverage', 'sectoral_retail', 'sectoral_service', 'food', 'restaurant', 'retail', 'service'].includes(effectiveSidebarType)))
                  ? "" // Admin, Sektörel, AI Post, Profil ve teslimat ayarlarında wrapper yok
                  : `${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-soft-xl rounded-xl lg:rounded-2xl bg-clip-border`
            }`}>
              {children}
            </div>
          </main>
        </div>
      </div>

      {/* 🏢 İŞLETME SEÇİM MODALI (Giriş yapılmış ama işletme seçilmemişse) */}
      <BusinessSelectionModal 
        key={(memberships || []).length > 0 ? 'with-data' : 'loading'} 
        isOpen={
          !isLoading &&
          isAuthenticated && 
          currentUser &&
          !isAdminUser(currentUser) && 
          !selectedBusiness && 
          !localStorage.getItem('selectedBusiness') &&
          !isPublicPage() && 
          location.pathname !== '/setup' && 
          (memberships || []).filter(m => m.business_id !== 'business_account' && m.id !== 'business_account').length > 0
        } 
      />

        {/* 🤖 AI Yönetim Asistanı & WhatsApp Hızlı Erişim (Sürüklenebilir & Varsayılan Kapalı) */}
      {isAuthenticated && !isPublicPage() && !location.pathname.includes('/whatsapp') && isMainHubPage(location.pathname) && (
        <DraggableFloatingWidget hideWidgets={hideWidgets} setHideWidgets={setHideWidgets} />
      )}

      {/* ⏰ CANLI HATIRLATICI POP-UP MODAL (Zamanı Geldiğinde Dikkat Çekici Uyarı) */}
      {activeReminderAlert && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in no-print">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border-2 border-amber-400 animate-bounce-short">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-5 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <BellAlertIcon className="w-6 h-6 text-white animate-pulse" />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-black tracking-widest text-amber-100">İşletme Asistanı</span>
                  <h3 className="font-extrabold text-base leading-tight">Hatırlatma Vakti Geldi!</h3>
                </div>
              </div>
              <button
                onClick={() => setActiveReminderAlert(null)}
                className="p-1.5 hover:bg-white/20 rounded-xl transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-amber-50/70 border border-amber-100 rounded-2xl p-4 space-y-2">
                <div className="flex items-center justify-between text-xs font-bold text-amber-800">
                  <span className="flex items-center gap-1.5">
                    <ClockIcon className="w-4 h-4 text-amber-600" />
                    <span>Saat: {activeReminderAlert.time || 'Belirtilmedi'}</span>
                  </span>
                  <span className="bg-amber-200/80 px-2.5 py-0.5 rounded-full text-[11px] font-black text-amber-900">
                    {activeReminderAlert.date || 'Bugün'}
                  </span>
                </div>
                <div className="text-base font-bold text-gray-900 leading-snug pt-1">
                  {activeReminderAlert.title}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => handleSnoozeReminderAlert(activeReminderAlert)}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95"
                >
                  <ClockIcon className="w-4 h-4 text-gray-500" />
                  <span>15 Dk Ertele</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleCompleteReminderAlert(activeReminderAlert.id)}
                  className="px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold rounded-2xl text-xs flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-600/30 transition-all active:scale-95"
                >
                  <CheckCircleIcon className="w-4 h-4 text-white" />
                  <span>Tamamlandı</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardLayout;